import { CheckboxControl, ComboBoxControl } from "@aurem/components";
import {
  Banner,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  type SelectOption,
} from "@aurem/ui";
import { useLanguageSwitcher } from "hooks/useLanguageSwitcher";
import { AlertTriangle, Globe } from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import { FormProvider, UseFormReturn } from "react-hook-form";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  citizenshipOptions: SelectOption[];
  blockedCitizenshipNames: string;
  onConfirm: () => void;
  form: UseFormReturn<{
    citizenships: SelectOption[];
    citizenshipConfirmation: boolean;
  }>;
};

export const CitizenshipConfirmationModal = ({
  open,
  onOpenChange,
  citizenshipOptions,
  blockedCitizenshipNames,
  onConfirm,
  form,
}: Props) => {
  const { t } = useI18n();
  const { currentLanguage, handleLanguageChange } = useLanguageSwitcher();

  const toggleLanguage = () => {
    const newLang = currentLanguage === "en" ? "ar" : "en";
    void handleLanguageChange(newLang);
  };

  return (
    <FormProvider {...form}>
      <Modal
        open={open}
        onOpenChange={onOpenChange}
        title={t("apps.employee.pages.citizenshipConfirmation.modal.title")}
        preventClose={true}
        showClose={false}
      >
        <ModalBody className="flex flex-col gap-4">
          <ComboBoxControl
            name="citizenships"
            options={citizenshipOptions}
            placeholder={t(
              "apps.employee.pages.citizenshipConfirmation.modal.placeholder",
            )}
            multiple
          />
          <CheckboxControl
            name="citizenshipConfirmation"
            required
            variant="card"
            className="bg-neutral-500/5"
            checkboxPosition="start"
            size="sm"
            label={t(
              "apps.employee.pages.citizenshipConfirmation.modal.checkbox",
              blockedCitizenshipNames,
            )}
          />
          <Banner
            color="warning"
            icon={<AlertTriangle />}
            variant="card"
            className="text-xs items-start"
            text={t(
              "apps.employee.pages.citizenshipConfirmation.modal.acknowledge",
            )}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            onClick={toggleLanguage}
            variant="ghost"
            color="neutral"
            size="sm"
            startIcon={<Globe className="w-5 h-5" />}
          >
            {currentLanguage.toUpperCase()}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={
              !form.watch("citizenships")?.length ||
              !form.watch("citizenshipConfirmation")
            }
          >
            {t("apps.employee.pages.citizenshipConfirmation.button")}
          </Button>
        </ModalFooter>
      </Modal>
    </FormProvider>
  );
};
